import { useEffect, useState } from "react";
import { create } from "zustand";
import { useNavigate } from "@tanstack/react-router";
import { MetricGrid } from "../console/metrics";
import { Activity, ArrowUpRight, Monitor, Smartphone } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, Field, Select, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can, isBillingOnly } from "../lib/access";
import { PublishList } from "./customer-publish";
import { compact, fmtDate, fmtDateTime, fmtTime, num, publishLabel, sanctionNames, serviceNames, serviceTone } from "../lib/format";
import { keys, useAction, useCustomerDetail, useDevices, useMachines, useMembers, useRuns } from "../lib/queries";
import type { CustomerDetail, CustomerStatus, Me, Member, TenantIntro } from "../lib/types";
import { ErrorBox, InfoRow, Loading, Panel, PanelError, StatusBadge, Tabs } from "../console/ui";
import { JobTitleField, jobTitleOf } from "../console/job-title";
import { AddMemberDialog } from "./customer";
import { RegisterMachineDialog } from "./machines";
import { SeoManagementScreen } from "./seo";
import { SeoConnectionSummary } from "./seo-ops";
import { CustomerUpdateDialog } from "./release";
import { jobTitle } from "./runs";
import { AssignRolesDialog, CustomerRolesPanel, RoleChips, useTenantRoles } from "./roles";
import { ManuscriptLibraryScreen } from "./manuscripts";
import { MaskedValue } from "../console/masked";

/**
 * 고객사 공통 상세 다이얼로그 (시안 06a1acb customer-detail.tsx 를 실제 API 로).
 * 목록 → 상세 탭 안에서 관리한다 (개요·기본 정보·상태·제재·플랜·기간·담당자·회원·SEO·머신·발행 요청).
 * 인라인 수정 + 저장하지 않은 변경 확인. 담당자 수정·삭제와 예외 한도 수정은 API 가 없어 숨긴다.
 */

export const useCustomerDetailFocus = create<{
  id: string | null;
  tab: string;
  seoTab: string;
  open: (id: string, tab?: string, seoTab?: string) => void;
  close: () => void;
}>((set) => ({
  id: null,
  tab: "개요",
  seoTab: "키워드",
  open: (id, tab = "개요", seoTab = "키워드") => set({ id, tab, seoTab }),
  close: () => set({ id: null }),
}));

export function CustomerDetailDialog({
  me,
  id,
  initialTab = "개요",
  initialSeoTab = "키워드",
  onClose,
  onEnter,
  onMachine,
  onOpenRun,
}: {
  me: Me;
  id: string | null;
  initialTab?: string;
  initialSeoTab?: string;
  onClose: () => void;
  onEnter: (id: string) => void;
  onMachine: (customerId: string, machineId: string) => void;
  onOpenRun: (runId: string, customerId: string) => void;
}) {
  const q = useCustomerDetail(id);
  // 원고 탭의 "정형화해서 올리기" 완료 → 정형화 탭, 준비 안 됨 → AI 실행 방식 (요청서 31절)
  const navigate = useNavigate();
  // SEO 하위 탭: 연결 요약 카드를 누르면 "연결" 로 (2026-10-21)
  const [seoTab, setSeoTab] = useState(initialSeoTab);
  useEffect(() => setSeoTab(initialSeoTab), [initialSeoTab, id]);
  const manage = can(me, "CUSTOMER_MANAGE");
  const billing = can(me, "BILLING_MANAGE");
  const tabs = [
    "개요",
    "기본 정보",
    ...(can(me, "CUSTOMER_STATUS", "MACHINE_SANCTION") ? ["상태·제재"] : []),
    "플랜·기간",
    "담당자",
    ...(can(me, "MEMBER_READ") ? ["회원"] : []),
    ...(can(me, "MARKETING_READ") ? ["SEO"] : []),
    ...(!isBillingOnly(me) ? ["머신"] : []),
    ...(can(me, "TASK_DETAIL") ? ["발행 요청"] : []),
    // 시안 ae6ed08: 원고(그 고객사 원고함)·권한(고객사 정책)
    ...(can(me, "MANUSCRIPT_READ") ? ["원고"] : []),
    ...(can(me, "SYSTEM_READ") ? ["권한"] : []),
  ];
  const [tab, setTab] = useState(initialTab);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [update, setUpdate] = useState(false);
  useEffect(() => {
    if (id) {
      setTab(tabs.includes(initialTab) ? initialTab : "개요");
      setDirty(false);
      setNote(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialTab]);
  const request = (next: string) => {
    if (dirty) setPending(next);
    else if (next === "close") onClose();
    else setTab(next);
  };
  const c = q.data;
  return (
    <Dialog
      open={!!id}
      onOpenChange={(v) => !v && request("close")}
      title={`${c?.name ?? ""} 관리 정보`}
      size="detail"
      description=""
      // 시안 4893fae (요청서 19절): 고객사 관리 이동은 다이얼로그 제목 옆 (모바일은 다음 줄)
      headerActions={
        c && (
          <Button size="sm" variant="secondary" onClick={() => onEnter(c.id)}>
            고객사 관리로 이동 <ArrowUpRight size={14} />
          </Button>
        )
      }
      footer={<DialogActions secondary={{ label: "닫기", onClick: () => request("close") }} note={note ?? undefined} />}
    >
      {q.isLoading ? (
        <Loading />
      ) : q.error || !c ? (
        <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : (
        <div className="lwd-stack">
          <div className="lwd-summary">
            <div className="lwd-identity">
              <div className="mcc-avatar">{c.name.slice(0, 1)}</div>
              <strong>{c.name}</strong>
              <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
            </div>
            <Badge>{c.subscription ? `${c.subscription.planName ?? c.subscription.planCode} · ${c.subscription.period === "year" ? "연간" : "월간"}` : "플랜 없음"}</Badge>
          </div>
          <Tabs items={tabs} value={tab} onChange={request} />
          {tab === "개요" && (
            <OverviewTab
              me={me}
              c={c}
              onEnter={() => onEnter(c.id)}
              onUpdate={() => setUpdate(true)}
              onTab={tabs.includes("머신") ? () => request("머신") : undefined}
              onSeo={
                tabs.includes("SEO")
                  ? () => {
                      setSeoTab("연결");
                      request("SEO");
                    }
                  : undefined
              }
            />
          )}
          {tab === "기본 정보" && <BasicTab c={c} editable={manage} onDirty={setDirty} onNote={setNote} />}
          {tab === "상태·제재" && <StatusTab me={me} c={c} onNote={setNote} />}
          {tab === "플랜·기간" && <PlanTab c={c} editable={billing} onDirty={setDirty} onNote={setNote} />}
          {tab === "담당자" && <ContactsTab c={c} editable={manage} onNote={setNote} />}
          {tab === "회원" && <MembersTab me={me} c={c} onNote={setNote} />}
          {tab === "원고" && (
            <ManuscriptLibraryScreen
              me={me}
              customerId={c.id}
              customerName={c.name}
              onDirtyChange={setDirty}
              onOpenRefineList={(ids) => void navigate({ to: "/admin/$screen", params: { screen: "refine" }, search: (ids.length ? { ids: ids.join(",") } : {}) as never })}
              onOpenAiSettings={() => void navigate({ to: "/admin/$screen", params: { screen: "ai-execution" } })}
            />
          )}
          {tab === "권한" && <CustomerRolesPanel me={me} customerId={c.id} customerName={c.name} suspended={!["active", "overdue"].includes(c.status)} onDirtyChange={setDirty} />}
          {tab === "SEO" && (
            <div className="lwd-stack">
              {/* 연결 요약 KPI 카드는 다른 화면처럼 바로 놓는다 (감싸는 패널 카드 없이, 2026-10-23 사용자) */}
              <SeoConnectionSummary customerId={c.id} onOpen={() => setSeoTab("연결")} />
              <SeoManagementScreen me={me} customerId={c.id} initialTab={seoTab} />
            </div>
          )}
          {tab === "머신" && <MachinesTab me={me} c={c} onMachine={(mid) => onMachine(c.id, mid)} />}
          {tab === "발행 요청" && <PublishRequestsTab c={c} onOpenRun={(r) => onOpenRun(r, c.id)} />}
          <CustomerUpdateDialog customerId={c.id} customerName={c.name} open={update} onClose={() => setUpdate(false)} onDone={setNote} />
        </div>
      )}
      <Dialog
        open={pending !== null}
        title="저장하지 않은 변경 사항"
        onOpenChange={(v) => !v && setPending(null)}
        footer={
          <DialogActions
            secondary={{ label: "계속 편집", onClick: () => setPending(null) }}
            primary={{
              label: "변경 버리고 이동",
              tone: "danger",
              onClick: () => {
                const next = pending;
                setDirty(false);
                setPending(null);
                if (next === "close") onClose();
                else if (next) setTab(next);
              },
            }}
          />
        }
      >
        <p>저장하지 않은 내용을 버리고 이동할까요?</p>
      </Dialog>
    </Dialog>
  );
}

function OverviewTab({ me, c, onEnter, onUpdate, onTab, onSeo }: { me: Me; c: CustomerDetail; onEnter: () => void; onUpdate: () => void; onTab?: () => void; onSeo?: () => void }) {
  const navigate = useNavigate();
  const machines = useMachines(c.id);
  const devices = useDevices(c.id);
  const live = (machines.data?.items ?? []).filter((m) => m.status !== "retired");
  return (
    <>
      {/* 카드를 누르면: 머신·디바이스 → 머신 탭, 이번 달 작업 → 그 고객사 작업 목록 (2026-10-21) */}
      <MetricGrid
        items={[
          { label: "머신", value: `${live.filter((m) => m.connected).length}/${live.length}`, icon: <Monitor />, onOpen: onTab, openLabel: "머신 탭에서 보기" },
          { label: "디바이스", value: devices.data?.total ?? 0, icon: <Smartphone />, onOpen: onTab, openLabel: "머신 탭에서 폰 보기" },
          ...(can(me, "TASK_DETAIL")
            ? [
                {
                  label: "이번 달 작업",
                  value: num(c.quota.used),
                  icon: <Activity />,
                  onOpen: () => void navigate({ to: "/c/$customerId/$screen", params: { customerId: c.id, screen: "tasks" } }),
                  openLabel: "이 고객사 작업 목록에서 보기",
                },
              ]
            : []),
        ]}
      />
      <Panel title="서비스">
        <InfoRow title={serviceNames[c.status]} description={`${c.statusReason ?? "사유 없음"} · ${fmtDateTime(c.statusChangedAt)} · 제재 ${c.sanctionLevel}단계 (${sanctionNames[c.sanctionLevel]})`}>
          <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
        </InfoRow>
        <InfoRow
          title={c.subscription ? `${c.subscription.planName ?? c.subscription.planCode} · ${c.subscription.period === "year" ? "연간" : "월간"}` : "플랜 없음"}
          description={c.subscription ? `${fmtDate(c.subscription.currentPeriodStart)} ~ ${fmtDate(c.subscription.currentPeriodEnd)} · ${c.subscription.daysLeft}일 남음` : ""}
        />
      </Panel>
      {can(me, "MARKETING_READ") && (
        <Panel title="SEO 연결">
          <SeoConnectionSummary customerId={c.id} onOpen={onSeo} />
        </Panel>
      )}
      <div className="lwd-actions">
        {can(me, "MACHINE_COMMAND") && (
          <Button variant="secondary" onClick={onUpdate}>
            모든 머신 업데이트
          </Button>
        )}
      </div>
    </>
  );
}

/** 기본 정보: 이름·사업자번호·AI 정책·테넌트 소개·발행 승인. 인라인 저장. 회원 권한은 권한 탭 (역할 정책). */
function BasicTab({ c, editable, onDirty, onNote }: { c: CustomerDetail; editable: boolean; onDirty: (v: boolean) => void; onNote: (m: string) => void }) {
  const [editing, setEditing] = useState(false);
  const settings = (c.settings ?? {}) as { intro?: TenantIntro; publishApproval?: "staff" | "auto" };
  const intro = settings.intro ?? {};
  const inv = [keys.customerDetail(c.id), keys.summary(c.id), ["admin", "customers"]];
  const save = useAction((b: Record<string, unknown>) => api.patch(`/admin/customers/${c.id}`, b), inv);
  const rows: [string, string][] = [
    ["고객사 이름", c.name],
    ["사업자번호", c.bizNo ?? "-"],
    ["AI 정책", c.aiPolicy],
    ["상호", intro.bizName ?? "-"],
    ["지역", intro.region ?? "-"],
    ["진료 분야 / 업종", intro.specialty ?? "-"],
    ["블로그 ID", intro.blogId ?? "-"],
    ["발행 승인", settings.publishApproval === "auto" ? "즉시 발행 (접수 즉시 실행)" : "관리자 확인 (기본)"],
  ];
  const cancel = () => {
    setEditing(false);
    onDirty(false);
  };
  return (
    <Panel
      title="기본 정보"
      extra={
        editable && !editing ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            수정
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <form
          className="lwd-panel-body mcc-form"
          onChange={() => onDirty(true)}
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            save
              .mutateAsync({
                name: String(d.get("name")).trim(),
                bizNo: String(d.get("bizNo") ?? "").trim() || null,
                aiPolicy: String(d.get("aiPolicy")),
                settings: {
                  intro: { bizName: String(d.get("bizName") ?? "").trim(), region: String(d.get("region") ?? "").trim(), specialty: String(d.get("specialty") ?? "").trim(), blogId: String(d.get("blogId") ?? "").trim() },
                  publishApproval: String(d.get("publishApproval")),
                },
              })
              .then(() => {
                cancel();
                onNote("기본 정보를 저장했습니다.");
              })
              .catch((er) => onNote(errorMessage(er)));
          }}
        >
          <div className="lwd-form-grid">
            <Field name="name" label="고객사 이름" defaultValue={c.name} required maxLength={100} />
            <Field name="bizNo" label="사업자번호" defaultValue={c.bizNo ?? ""} />
            <Select name="aiPolicy" label="AI 정책" defaultValue={c.aiPolicy}>
              <option value="own">고객사 계정 (own)</option>
              <option value="shared">공용 계정 (shared)</option>
            </Select>
            <Select name="publishApproval" label="발행 승인 (고객 발행 요청)" defaultValue={settings.publishApproval ?? "staff"}>
              <option value="staff">관리자 확인 뒤 실행</option>
              <option value="auto">즉시 발행 (형식에 맞는 원고는 접수 즉시 실행)</option>
            </Select>
            <Field name="bizName" label="상호 (테넌트 소개)" defaultValue={intro.bizName ?? ""} />
            <Field name="region" label="지역" defaultValue={intro.region ?? ""} />
            <Field name="specialty" label="진료 분야 / 업종" defaultValue={intro.specialty ?? ""} />
            <Field name="blogId" label="블로그 ID" defaultValue={intro.blogId ?? ""} />
          </div>
          <div className="lwd-actions">
            <Button variant="secondary" onClick={cancel}>
              취소
            </Button>
            <Button type="submit" disabled={save.isPending}>
              저장
            </Button>
          </div>
        </form>
      ) : (
        rows.map(([k, v]) => <InfoRow key={k} title={k} description={v} />)
      )}
    </Panel>
  );
}

function StatusTab({ me, c, onNote }: { me: Me; c: CustomerDetail; onNote: (m: string) => void }) {
  const inv = [keys.customerDetail(c.id), ["admin"], ["c", c.id]];
  const status = useAction((b: { status: CustomerStatus; reason?: string }) => api.post(`/admin/customers/${c.id}/status`, b), inv);
  const sanction = useAction((level: number) => api.post(`/admin/customers/${c.id}/sanction`, { level }), inv);
  const [confirm, setConfirm] = useState<{ status: CustomerStatus; reason?: string } | null>(null);
  const run = (p: Promise<unknown>, done: string) => p.then(() => onNote(done)).catch((e) => onNote(errorMessage(e)));
  return (
    <>
      <Panel title="서비스 상태와 제재">
        <InfoRow title={serviceNames[c.status]} description={`${c.statusReason ?? "사유 없음"} · ${fmtDateTime(c.statusChangedAt)} · 제재 ${c.sanctionLevel}단계 (${sanctionNames[c.sanctionLevel]})`}>
          <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
        </InfoRow>
        {can(me, "CUSTOMER_STATUS") && (
          <form
            className="lwd-panel-body mcc-form"
            onSubmit={(e) => {
              e.preventDefault();
              const d = new FormData(e.currentTarget);
              const next = d.get("status") as CustomerStatus;
              const reason = String(d.get("reason") ?? "") || undefined;
              if (next === "terminated") setConfirm({ status: next, reason });
              else void run(status.mutateAsync({ status: next, reason }), "상태를 바꿨습니다. 머신에 바로 적용됩니다.");
            }}
          >
            <div className="lwd-form-grid">
              <Select label="서비스 상태" name="status" defaultValue={c.status}>
                {(["active", "overdue", "suspended", "terminated"] as CustomerStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {serviceNames[s]}
                  </option>
                ))}
              </Select>
              <Field name="reason" label="변경 사유" placeholder="예: 10월 미결제" />
            </div>
            <Button type="submit" variant="secondary" disabled={status.isPending}>
              상태 변경 저장
            </Button>
          </form>
        )}
        {can(me, "MACHINE_SANCTION") && (
          <InfoRow title="제재 단계" description="0 없음 · 1 새 작업 중지 · 2 실행 중 작업 취소 · 3 프로그램 잠금">
            <div className="lwd-actions">
              {[0, 1, 2, 3].map((l) => (
                <Button key={l} size="sm" variant={l === c.sanctionLevel ? "primary" : "secondary"} onClick={() => void run(sanction.mutateAsync(l), `제재를 ${l}단계로 바꿨습니다.`)}>
                  {l}
                </Button>
              ))}
            </div>
          </InfoRow>
        )}
      </Panel>
      <Panel title="변경 이력">
        <InfoRow title="감사 기록에서 확인" description="상태·제재 변경은 모니터링 › 감사 기록에 남습니다 (고객사별 필터는 다음 단계)." />
      </Panel>
      <Dialog
        open={!!confirm}
        title="해지 확인"
        description={c.name}
        onOpenChange={(v) => !v && setConfirm(null)}
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setConfirm(null) }}
            primary={{
              label: "해지",
              tone: "danger",
              onClick: () => {
                const b = confirm!;
                setConfirm(null);
                void run(status.mutateAsync(b), "해지했습니다. 모든 머신의 연결 자격이 폐기됩니다.");
              },
            }}
          />
        }
      >
        <p>고객사를 해지하면 모든 머신의 페어링이 끊기고 회원 세션이 종료됩니다. 복구는 active 로만 할 수 있습니다.</p>
      </Dialog>
    </>
  );
}

function PlanTab({ c, editable, onDirty, onNote }: { c: CustomerDetail; editable: boolean; onDirty: (v: boolean) => void; onNote: (m: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState(c.subscription?.planCode ?? "basic");
  // 프로모션은 연간 고정·결제 없음: 주기는 고를 게 없고, 새로 줄 때는 오늘부터 1년이라 종료일도 정하지 않는다
  const promo = picked === "promo";
  const toPromo = promo && c.subscription?.planCode !== "promo";
  const promoEnd = new Date(Date.now() + 365 * 86400_000).toISOString();
  const inv = [keys.customerDetail(c.id), ["admin"], ["c", c.id]];
  const plan = useAction((b: { planCode: string; period: string }) => api.post(`/admin/customers/${c.id}/plan`, b), inv);
  const period = useAction((end: string) => api.put(`/admin/customers/${c.id}/subscription`, { currentPeriodEnd: end }), inv);
  const cancel = () => {
    setEditing(false);
    setPicked(c.subscription?.planCode ?? "basic");
    onDirty(false);
  };
  return (
    <Panel
      title="플랜·기간"
      extra={
        editable && !editing ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            수정
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <form
          className="lwd-panel-body mcc-form"
          onChange={() => onDirty(true)}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            const planCode = String(d.get("planCode"));
            const per = promo ? "year" : String(d.get("period"));
            const end = String(d.get("end") ?? "");
            try {
              if (planCode !== (c.subscription?.planCode ?? "") || per !== (c.subscription?.period ?? "")) await plan.mutateAsync({ planCode, period: per });
              if (end && end !== (c.subscription?.currentPeriodEnd.slice(0, 10) ?? "")) await period.mutateAsync(new Date(`${end}T23:59:59+09:00`).toISOString());
              cancel();
              onNote("플랜·기간을 저장했습니다. 정지 상태였다면 정상으로 돌아갑니다.");
            } catch (er) {
              onNote(errorMessage(er));
            }
          }}
        >
          <div className="lwd-form-grid">
            <Select label="플랜" name="planCode" value={picked} onChange={(e) => setPicked(e.target.value)}>
              <option value="trial">체험</option>
              <option value="basic">기본</option>
              <option value="pro">프로</option>
              <option value="promo">프로모션 (프로 1년 무료, 연간 고정)</option>
              <option value="enterprise">엔터프라이즈</option>
            </Select>
            {!promo && (
              <Select label="주기" name="period" defaultValue={c.subscription?.period ?? "month"}>
                <option value="month">월간</option>
                <option value="year">연간</option>
              </Select>
            )}
            {!toPromo && <Field name="end" label="구독 종료일" type="date" defaultValue={c.subscription ? c.subscription.currentPeriodEnd.slice(0, 10) : undefined} required />}
          </div>
          {toPromo && <p className="meta-crew-muted">{`저장하면 오늘부터 1년(${fmtDate(promoEnd)}까지) 프로와 같은 한도로 무료 이용합니다. 지금 구독은 끝나고, 청구서는 발행되지 않습니다.`}</p>}
          <p className="meta-crew-muted">예외 머신·디바이스 한도는 요금 정책 확정 뒤 열립니다. 작업 횟수 한도는 적용하지 않습니다.</p>
          <div className="lwd-actions">
            <Button variant="secondary" onClick={cancel}>
              취소
            </Button>
            <Button type="submit" disabled={plan.isPending || period.isPending}>
              저장
            </Button>
          </div>
        </form>
      ) : (
        <>
          <InfoRow title="플랜" description={c.subscription ? `${c.subscription.planName ?? c.subscription.planCode} · ${c.subscription.period === "year" ? "연간" : "월간"}` : "플랜 없음"} />
          <InfoRow title="구독 기간" description={c.subscription ? `${fmtDate(c.subscription.currentPeriodStart)} ~ ${fmtDate(c.subscription.currentPeriodEnd)} · ${c.subscription.daysLeft}일 남음` : "-"} />
          <InfoRow title="한도" description={`머신 ${c.quota.machines ?? "무제한"} · 디바이스 ${c.quota.devices ?? "무제한"} · 이번 달 작업 ${num(c.quota.used)}건 · 토큰 ${compact(c.quota.tokens.input + c.quota.tokens.output)}`} />
        </>
      )}
    </Panel>
  );
}

function ContactsTab({ c, editable, onNote }: { c: CustomerDetail; editable: boolean; onNote: (m: string) => void }) {
  const [adding, setAdding] = useState(false);
  const contact = useAction((b: Record<string, unknown>) => api.post(`/admin/customers/${c.id}/contacts`, b), [keys.customerDetail(c.id), ["admin", "customers"]]);
  return (
    <Panel
      title="담당자"
      extra={
        editable && !adding ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            담당자 추가
          </Button>
        ) : undefined
      }
    >
      {c.contacts.length === 0 && <InfoRow title="등록된 담당자가 없습니다" />}
      {c.contacts.map((k) => (
        <InfoRow
          key={k.customerContactNo}
          title={
            <>
              <MaskedValue value={k.name} source={{ url: `/admin/customers/${c.id}/contacts/${k.customerContactNo}/pii`, pick: (d) => d.name }} />
              {k.jobTitle ? ` · ${k.jobTitle}` : null}
            </>
          }
          description={
            k.phone || k.email || k.memo ? (
              <>
                {k.phone && <MaskedValue value={k.phone} source={{ url: `/admin/customers/${c.id}/contacts/${k.customerContactNo}/pii`, pick: (d) => d.phone }} />}
                {k.phone && k.email ? " · " : null}
                {k.email && <MaskedValue value={k.email} source={{ url: `/admin/customers/${c.id}/contacts/${k.customerContactNo}/pii`, pick: (d) => d.email }} />}
                {k.memo ? ` · ${k.memo}` : null}
              </>
            ) : (
              "연락처 없음"
            )
          }
        />
      ))}
      {adding && (
        <form
          className="lwd-panel-body mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            contact
              .mutateAsync({ name: d.get("name"), phone: d.get("phone") || undefined, email: d.get("email") || undefined, jobTitle: jobTitleOf(d) || undefined, role: "onsite" })
              .then(() => {
                setAdding(false);
                onNote("담당자를 추가했습니다.");
              })
              .catch((er) => onNote(errorMessage(er)));
          }}
        >
          <div className="lwd-form-grid">
            <Field name="name" label="이름" required />
            <Field name="phone" label="연락처" />
            <Field name="email" label="이메일" type="email" />
            <JobTitleField />
          </div>
          <div className="lwd-actions">
            <Button variant="secondary" onClick={() => setAdding(false)}>
              취소
            </Button>
            <Button type="submit" disabled={contact.isPending}>
              추가
            </Button>
          </div>
        </form>
      )}
      <div className="lwd-panel-body">
        <p className="meta-crew-muted">담당자 수정·삭제는 API 가 준비되면 열립니다.</p>
      </div>
    </Panel>
  );
}

function MembersTab({ me, c, onNote }: { me: Me; c: CustomerDetail; onNote: (m: string) => void }) {
  const q = useMembers(c.id);
  const [invite, setInvite] = useState(false);
  const [reset, setReset] = useState<{ id: string; name: string } | null>(null);
  const [titleOf, setTitleOf] = useState<{ id: string; name: string; jobTitle: string | null } | null>(null);
  const [assign, setAssign] = useState<Member | null>(null);
  const roles = useTenantRoles(c.id);
  const roleOptions = roles.data?.roles ?? [];
  const manage = can(me, "MEMBER_MANAGE");
  const inv = [keys.members(c.id)];
  const patch = useAction((v: { id: string; body: Record<string, unknown> }) => api.patch(`/c/${c.id}/members/${v.id}`, v.body), inv);
  const password = useAction((v: { id: string; password: string }) => api.post(`/c/${c.id}/members/${v.id}/password`, { password: v.password }), inv);
  const run = (p: Promise<unknown>, done: string) => p.then(() => onNote(done)).catch((e) => onNote(errorMessage(e)));
  return (
    <Panel
      title="고객사 회원"
      extra={
        manage && (
          <Button size="sm" onClick={() => setInvite(true)}>
            회원 초대
          </Button>
        )
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : (q.data?.items ?? []).length === 0 ? (
        <InfoRow title="아직 고객사 회원이 없습니다" description="고객 화면은 관리자가 대신 관리합니다." />
      ) : (
        <Table>
          <thead>
            <tr>
              <th>이름 / 이메일</th>
              <th>역할</th>
              <th>상태</th>
              <th>마지막 로그인</th>
              {manage && <th>관리</th>}
            </tr>
          </thead>
          <tbody>
            {(q.data?.items ?? []).map((m) => (
              <tr key={m.id}>
                <td>
                  <MaskedValue value={m.name} source={{ url: `/c/${c.id}/members/${m.id}/pii`, pick: (d) => d.name }} />
                  {m.jobTitle ? ` · ${m.jobTitle}` : null}
                  <small className="meta-crew-sub">
                    <MaskedValue value={m.email} source={{ url: `/c/${c.id}/members/${m.id}/pii`, pick: (d) => d.email }} />
                  </small>
                </td>
                <td>
                  <RoleChips roles={m.roles} />
                </td>
                <td>
                  <Badge tone={m.status === "active" ? "teal" : "neutral"}>{m.status === "active" ? "활성" : "중지"}</Badge>
                </td>
                <td>{m.lastLoginAt ? fmtDateTime(m.lastLoginAt) : "-"}</td>
                {manage && (
                  <td>
                    <div className="lwd-actions">
                      <Button size="sm" variant="secondary" onClick={() => void run(patch.mutateAsync({ id: m.id, body: { status: m.status === "active" ? "disabled" : "active" } }), m.status === "active" ? "중지했습니다." : "활성화했습니다.")}>
                        {m.status === "active" ? "중지" : "활성"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAssign(m)}>
                        역할 지정
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setTitleOf({ id: m.id, name: m.name, jobTitle: m.jobTitle ?? null })}>
                        직책
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setReset({ id: m.id, name: m.name })}>
                        비밀번호 재설정
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <AddMemberDialog
        open={invite}
        onClose={() => setInvite(false)}
        path={`/c/${c.id}/members`}
        roleOptions={roleOptions}
        defaultHint="고르지 않으면 첫 회원은 소유자, 그다음은 멤버"
        invalidate={keys.members(c.id)}
      />
      <AssignRolesDialog
        open={!!assign}
        onClose={() => setAssign(null)}
        memberName={assign?.name ?? ""}
        options={roleOptions}
        current={assign?.roles ?? []}
        path={`/c/${c.id}/members/${assign?.id ?? ""}/roles`}
        invalidate={[[...keys.members(c.id)]]}
        onDone={onNote}
      />
      <Dialog
        open={!!titleOf}
        title="직책 바꾸기"
        description={titleOf?.name ?? ""}
        onOpenChange={(v) => !v && setTitleOf(null)}
        footer={<DialogActions secondary={{ label: "취소", onClick: () => setTitleOf(null) }} primary={{ label: "저장", type: "submit", form: "customer-detail-job-title" }} />}
      >
        <form
          id="customer-detail-job-title"
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            const target = titleOf!;
            const jobTitle = jobTitleOf(new FormData(e.currentTarget));
            setTitleOf(null);
            void run(patch.mutateAsync({ id: target.id, body: { jobTitle } }), "직책을 바꿨습니다.");
          }}
        >
          {titleOf && <JobTitleField key={titleOf.id} defaultValue={titleOf.jobTitle} />}
        </form>
      </Dialog>
      <Dialog
        open={!!reset}
        title="비밀번호 재설정"
        description={reset?.name ?? ""}
        onOpenChange={(v) => !v && setReset(null)}
        footer={<DialogActions secondary={{ label: "취소", onClick: () => setReset(null) }} primary={{ label: "재설정", type: "submit", form: "customer-detail-password" }} />}
      >
        <form
          id="customer-detail-password"
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            const pw = String(new FormData(e.currentTarget).get("password") ?? "");
            const target = reset!;
            setReset(null);
            void run(password.mutateAsync({ id: target.id, password: pw }), "비밀번호를 재설정했습니다. 본인에게 따로 전달하세요.");
          }}
        >
          <Field name="password" label="새 비밀번호 (10자 이상)" type="password" minLength={10} required autoComplete="new-password" />
        </form>
      </Dialog>
    </Panel>
  );
}

function MachinesTab({ me, c, onMachine }: { me: Me; c: CustomerDetail; onMachine: (machineId: string) => void }) {
  const q = useMachines(c.id);
  const [pair, setPair] = useState(false);
  const rows = (q.data?.items ?? []).filter((m) => m.status !== "retired");
  return (
    <Panel
      title="고객사 머신"
      extra={
        can(me, "MACHINE_PAIR") && (
          <Button size="sm" onClick={() => setPair(true)}>
            머신 등록
          </Button>
        )
      }
    >
      {q.isLoading ? <Loading /> : q.error ? <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} /> : rows.length === 0 ? <InfoRow title="등록된 머신이 없습니다" /> : null}
      {rows.map((m) => (
        <InfoRow key={m.id} title={m.label} description={`${m.version ? `v${m.version}` : "버전 정보 없음"} · 동시 실행 ${m.maxConcurrent ?? "-"} · 마지막 수신 ${fmtTime(m.lastSeenAt)}`}>
          <Badge tone={m.connected ? "teal" : m.pairedAt ? "coral" : "neutral"}>{m.connected ? "온라인" : m.pairedAt ? "연결 끊김" : "미연결"}</Badge>
          <Button size="sm" variant="secondary" onClick={() => onMachine(m.id)}>
            머신 상세
          </Button>
        </InfoRow>
      ))}
      <RegisterMachineDialog customerId={c.id} open={pair} onClose={() => setPair(false)} />
    </Panel>
  );
}

/** 발행 요청 탭 (시안 951f2f6 staff-publish-queue): 고객이 보낸 요청(origin=customer) 카드 목록. 상세에서 승인·수정 요청·반려. */
function PublishRequestsTab({ c, onOpenRun }: { c: CustomerDetail; onOpenRun: (runId: string) => void }) {
  return <PublishList customerId={c.id} staff onOpen={onOpenRun} />;
}
